import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import SingleSelect from "@/components/controls/SingleSelect";
import DateTimeField from "@/components/controls/DateTimeField";
import Tabs from "@/components/controls/Tabs";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Delete } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import SingleInput from "@/components/controls/SingleInput";
import { formatFullDate, toDateOnlyISO } from "@/utils/datetime";
import { formatCurrency, formatBillingStartDate } from "../consts";
import { EqPaymentsBillingFrequency } from "@/pages/contracts/types";
import { PaymentType, ScheduledPaymentDraft } from "../types";

interface PaymentScheduleProps {
  totalAmount: number;
  billingStartDate: string;
  currency?: string | null;
  mode?: "one-time" | "recurring";
  // The tab and the rows are owned by the drawer form: both have to survive
  // the drawer's submit and be restored when an existing service is reopened.
  paymentType: PaymentType;
  scheduledPayments: ScheduledPaymentDraft[];
  // The one-time equal split is owned by the drawer form too, so a reopened
  // line shows the split it saved rather than the defaults.
  numberOfPayments?: number;
  billingFrequency?: EqPaymentsBillingFrequency;
  onOneTimeEqualPaymentChange?: (
    numberOfPayments: number,
    billingFrequency: EqPaymentsBillingFrequency
  ) => void;
  onRecurringEqualPaymentChange?: (
    billingEvery: number,
    period: EqPaymentsBillingFrequency
  ) => void;
  onScheduledPaymentsChange: (payments: ScheduledPaymentDraft[]) => void;
  onPaymentTypeChange: (type: PaymentType) => void;
  onCancelSplit?: () => void;
}

const PaymentSchedule: React.FC<PaymentScheduleProps> = ({
  totalAmount,
  billingStartDate,
  currency = "USD",
  mode = "recurring",
  paymentType,
  scheduledPayments,
  numberOfPayments = 2,
  billingFrequency = EqPaymentsBillingFrequency.MONTHS,
  onOneTimeEqualPaymentChange,
  onRecurringEqualPaymentChange,
  onScheduledPaymentsChange,
  onPaymentTypeChange,
  onCancelSplit
}) => {
  const { t } = useTranslation();

  const BILLING_FREQUENCY_OPTIONS = [
    {
      id: EqPaymentsBillingFrequency.DAYS,
      displayName: t("contractTerms.billingFrequencyLabels.days")
    },
    {
      id: EqPaymentsBillingFrequency.WEEKS,
      displayName: t("contractTerms.billingFrequencyLabels.weeks")
    },
    {
      id: EqPaymentsBillingFrequency.MONTHS,
      displayName: t("contractTerms.billingFrequencyLabels.months")
    },
    {
      id: EqPaymentsBillingFrequency.YEARS,
      displayName: t("contractTerms.billingFrequencyLabels.years")
    }
  ];

  const PERIOD_OPTIONS = [
    {
      id: EqPaymentsBillingFrequency.DAYS,
      displayName: t("contractTerms.billingFrequencyLabels.days")
    },
    {
      id: EqPaymentsBillingFrequency.WEEKS,
      displayName: t("contractTerms.billingFrequencyLabels.weeks")
    },
    {
      id: EqPaymentsBillingFrequency.MONTHS,
      displayName: t("contractTerms.billingFrequencyLabels.months")
    },
    {
      id: EqPaymentsBillingFrequency.YEARS,
      displayName: t("contractTerms.billingFrequencyLabels.years")
    }
  ];

  // State for recurring mode
  const [billingEvery, setBillingEvery] = useState<number>(2);
  const [period, setPeriod] = useState<EqPaymentsBillingFrequency>(
    EqPaymentsBillingFrequency.MONTHS
  );
  const [openDatePickerIndex, setOpenDatePickerIndex] = useState<number | null>(
    null
  );

  const allocatedAmount = useMemo(() => {
    return scheduledPayments.reduce((sum, payment) => sum + payment.amount, 0);
  }, [scheduledPayments]);

  const remainingAmount = useMemo(() => {
    return totalAmount - allocatedAmount;
  }, [totalAmount, allocatedAmount]);

  // Handlers for one-time mode
  const handleNumberOfPaymentsChange = (value: number) => {
    const newValue = Math.max(1, value);
    onOneTimeEqualPaymentChange?.(newValue, billingFrequency);
  };

  const handleBillingFrequencyChange = (value: string | number) => {
    const newFrequency = Number(value) as EqPaymentsBillingFrequency;
    onOneTimeEqualPaymentChange?.(numberOfPayments, newFrequency);
  };

  // Handlers for recurring mode
  const handleBillingEveryChange = (value: number) => {
    const newValue = Math.max(1, value);
    setBillingEvery(newValue);
    onRecurringEqualPaymentChange?.(newValue, period);
  };

  const handlePeriodChange = (value: string | number) => {
    const newPeriod = Number(value) as EqPaymentsBillingFrequency;
    setPeriod(newPeriod);
    onRecurringEqualPaymentChange?.(billingEvery, newPeriod);
  };

  const paymentAmount = useMemo(() => {
    return numberOfPayments > 0 ? totalAmount / numberOfPayments : 0;
  }, [totalAmount, numberOfPayments]);

  const paymentAmountDecimals = Number.isInteger(paymentAmount) ? 0 : 2;

  const getBillingFrequencyLabel = (
    frequency: EqPaymentsBillingFrequency
  ): string => {
    switch (frequency) {
      case EqPaymentsBillingFrequency.DAYS:
        return t("contractTerms.billingFrequencyLabels.days").toLowerCase();
      case EqPaymentsBillingFrequency.WEEKS:
        return t("contractTerms.billingFrequencyLabels.weeks").toLowerCase();
      case EqPaymentsBillingFrequency.MONTHS:
        return t("contractTerms.billingFrequencyLabels.months").toLowerCase();
      case EqPaymentsBillingFrequency.YEARS:
        return t("contractTerms.billingFrequencyLabels.years").toLowerCase();
      default:
        return "";
    }
  };

  const handleAddPayment = () => {
    const newPayment: ScheduledPaymentDraft = {
      id: String(Date.now()),
      billingDate: billingStartDate || toDateOnlyISO(new Date()),
      amount: 0,
      percentage: 0
    };
    onScheduledPaymentsChange([...scheduledPayments, newPayment]);
  };

  const handleDeletePayment = (id: string) => {
    onScheduledPaymentsChange(
      scheduledPayments.filter((payment) => payment.id !== id)
    );
  };

  const handlePaymentChange = (
    index: number,
    field: keyof ScheduledPaymentDraft,
    value: string | number
  ) => {
    const updatedPayments = [...scheduledPayments];

    if (field === "amount" || field === "percentage") {
      const numValue =
        typeof value === "string" ? parseFloat(value) || 0 : value;
      const requestedAmount =
        field === "amount" ? numValue : (numValue / 100) * totalAmount;
      const otherPaymentsAmount =
        allocatedAmount - updatedPayments[index].amount;
      const cappedAmount = Math.min(
        Math.max(0, requestedAmount),
        Math.max(0, totalAmount - otherPaymentsAmount)
      );
      updatedPayments[index] = {
        ...updatedPayments[index],
        amount: cappedAmount,
        percentage: totalAmount > 0 ? (cappedAmount / totalAmount) * 100 : 0
      };
    } else if (field === "billingDate") {
      updatedPayments[index] = {
        ...updatedPayments[index],
        billingDate: value as string
      };
    }

    onScheduledPaymentsChange(updatedPayments);
  };

  return (
    <div className="bg-surface-elevation2 border-[0.5px] border-border-border1 rounded-xl p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Tabs
          options={{
            equal: t("contractTerms.paymentSchedule.equalPayments"),
            scheduled: t("contractTerms.paymentSchedule.scheduledPayments")
          }}
          value={paymentType}
          valueSelected={(value) => onPaymentTypeChange(value as PaymentType)}
          containerClassName="w-[272px]"
        />
        {onCancelSplit && (
          <Button variant="tertiary" onClick={onCancelSplit}>
            {t("contractTerms.paymentSchedule.cancelSplit")}
          </Button>
        )}
      </div>

      {paymentType === "equal" && mode === "one-time" && (
        <>
          <div className="flex flex-row gap-4">
            <div className="flex flex-col gap-2 flex-1">
              <Text variant="bodySecondary">
                {t("contractTerms.paymentSchedule.numberOfPayments")}
              </Text>
              <Input
                type="number"
                value={numberOfPayments}
                onChange={(e) =>
                  handleNumberOfPaymentsChange(parseInt(e.target.value) || 1)
                }
                min={1}
              />
            </div>
            <div className="flex flex-col gap-2 flex-1">
              <Text variant="bodySecondary">
                {t("contractTerms.billingFrequency")}
              </Text>
              <SingleSelect
                fieldName="billing-frequency"
                options={BILLING_FREQUENCY_OPTIONS}
                selectedValue={billingFrequency}
                onSelection={handleBillingFrequencyChange}
                variant="form"
                className="w-full"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1 pt-2">
            <Text variant="bodySecondary">{t("contractTerms.summary")}</Text>
            <Text variant="body">
              {t("contractTerms.paymentSchedule.paymentsOfAmount", {
                count: numberOfPayments,
                amount: formatCurrency(
                  paymentAmount,
                  currency,
                  paymentAmountDecimals
                )
              })}
            </Text>
            <Text variant="bodySecondary">
              {t("contractTerms.paymentSchedule.billedFrequencyStarting", {
                frequency: getBillingFrequencyLabel(billingFrequency),
                date: formatBillingStartDate(billingStartDate)
              })}
            </Text>
          </div>
        </>
      )}

      {paymentType === "equal" && mode === "recurring" && (
        <div className="flex flex-row gap-2 items-center">
          <Text variant="body">
            {t("contractTerms.paymentSchedule.billingEvery")}
          </Text>
          <Input
            type="number"
            value={billingEvery}
            onChange={(e) =>
              handleBillingEveryChange(parseInt(e.target.value) || 1)
            }
            min={1}
            className="w-20"
          />
          <SingleSelect
            fieldName="billing-period"
            options={PERIOD_OPTIONS}
            selectedValue={period}
            onSelection={handlePeriodChange}
            variant="form"
            className="w-32"
          />
        </div>
      )}

      {paymentType === "scheduled" && (
        <div className="flex flex-col">
          <div className="border-[0.5px] border-border-border1 rounded-lg overflow-hidden">
            <div className="bg-surface-elevation2 border-b border-border-border1">
              <div className="flex px-4 py-3">
                <div className="w-[60px]">
                  <Text variant="smLabel" className="text-secondary">
                    {t("contractTerms.paymentSchedule.payment")}
                  </Text>
                </div>
                <div className="w-[140px]">
                  <Text variant="smLabel" className="text-secondary">
                    {t("contractTerms.paymentSchedule.billingDate")}
                  </Text>
                </div>
                <div className="w-[120px]">
                  <Text variant="smLabel" className="text-secondary">
                    {t("contractTerms.paymentSchedule.amountDollar")}
                  </Text>
                </div>
                <div className="w-[120px]">
                  <Text variant="smLabel" className="text-secondary">
                    {t("contractTerms.paymentSchedule.percentageSymbol")}
                  </Text>
                </div>
                <div className="flex-1"></div>
              </div>
            </div>

            <div className="divide-y divide-border-border1">
              {scheduledPayments.map((payment, index) => (
                <div
                  key={payment.id}
                  className="flex items-center px-2 bg-surface-elevation2 hover:bg-surface-hover-soft transition-colors min-h-[44px]"
                >
                  <div className="w-[60px] flex items-center justify-center">
                    <Text variant="body">{index + 1}</Text>
                  </div>
                  <div className="w-[140px] flex items-center">
                    {openDatePickerIndex === index ? (
                      <DateTimeField
                        type="date"
                        value={payment.billingDate}
                        onChange={(value) =>
                          handlePaymentChange(index, "billingDate", value)
                        }
                        isOpen={true}
                        onOpenChange={(open) =>
                          setOpenDatePickerIndex(open ? index : null)
                        }
                        name={`payment-date-${index}`}
                        className="border-transparent"
                      />
                    ) : (
                      <div
                        className="hover:bg-surface-hover-strong rounded-lg cursor-pointer w-full p-2 min-h-9 flex items-center"
                        onClick={() => setOpenDatePickerIndex(index)}
                      >
                        <Text variant="body">
                          {payment.billingDate
                            ? formatFullDate(payment.billingDate, true, false)
                            : t("contractTerms.paymentSchedule.selectDate")}
                        </Text>
                      </div>
                    )}
                  </div>
                  <div className="w-[120px] flex items-center">
                    <SingleInput
                      fieldName={`payment-amount-${index}`}
                      // No currency sign in the value: it is passed as
                      // `currencySymbol` below and the collapsed text adds it
                      // back. In the value it was a character the field's own
                      // guard rejects, so the amount was not typeable.
                      initialValue={
                        payment.amount ? payment.amount.toFixed(2) : ""
                      }
                      onSave={(value) => {
                        const numValue =
                          parseFloat(value.replace(/[$,]/g, "")) || 0;
                        handlePaymentChange(index, "amount", numValue);
                      }}
                      isMonetary={true}
                      currencySymbol="$"
                      placeholder="$0"
                      textVariant="body"
                      width="100%"
                    />
                  </div>
                  <div className="w-[120px] flex items-center">
                    <SingleInput
                      fieldName={`payment-percentage-${index}`}
                      initialValue={
                        payment.percentage
                          ? `${payment.percentage.toFixed(1)}%`
                          : ""
                      }
                      onSave={(value) => {
                        const numValue =
                          parseFloat(value.replace(/%/g, "")) || 0;
                        handlePaymentChange(index, "percentage", numValue);
                      }}
                      placeholder="0%"
                      textVariant="body"
                      width="100%"
                    />
                  </div>
                  <div className="flex-1 flex items-center justify-end">
                    {scheduledPayments.length > 1 && (
                      <IconButton
                        variant="iconNoBg"
                        iconColor="secondary"
                        size="sm"
                        onClick={() => handleDeletePayment(payment.id)}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex items-center bg-surface-elevation2 hover:bg-surface-hover-soft h-[44px] transition-colors">
                <div className="flex-1 flex items-center">
                  <Button
                    variant="tertiary"
                    className="ml-2"
                    onClick={handleAddPayment}
                    disabled={remainingAmount <= 0}
                  >
                    {t("contractTerms.paymentSchedule.addPayment")}
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between px-4 py-3  bg-surface-elevation3">
              <Text variant="bodySecondary">
                {t("contractTerms.paymentSchedule.remainingToAllocate")}
              </Text>
              <Text
                variant="label"
                className={cn(
                  remainingAmount > 0 ? "text-error" : "text-positive"
                )}
              >
                {formatCurrency(remainingAmount, currency)}
              </Text>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSchedule;
